/**
 * Die Fehlerseite der ganzen Anwendung (Spec Mails & Feedback s6, D13/F14;
 * Plan s6 A7).
 *
 * Sie hängt als `errorElement` an der äußersten Route und fängt damit jeden
 * Absturz beim Zeichnen einer Seite — und jede Adresse, zu der es keine
 * Route gibt. Bis zum 08.10.2026 zeigte react-router an dieser Stelle seine
 * eingebaute Entwicklerseite mit Stacktrace, englisch und ohne Weg zurück
 * (der Wortlaut steht im Test).
 *
 * - **Eigenständig gestylt, ohne Navigation:** Bei einem Absturz kann die
 *   Hülle mit Reiterleiste oder Seitenleiste selbst das Kaputte sein. Die
 *   Seite braucht deshalb nichts als die Grundfarben.
 * - **„Neu laden"** lädt die ganze Seite neu — das behebt den häufigsten
 *   Fall, eine nach einer Auslieferung veraltete Seite.
 * - **„Problem melden"** nur für angemeldete Lehrkräfte (D13): Nur dort gibt
 *   es das Formular. Es öffnet `/e/feedback`, vorbelegt mit Thema, Seite und
 *   Fehlermeldung (D12: kein Stacktrace). Ein echter Seitenaufruf statt eines
 *   `<Link>`: Der Zustand der Anwendung ist nach einem Absturz nicht mehr
 *   vertrauenswürdig, und ein nicht nachladbarer Code-Teil wäre es beim
 *   zweiten Versuch auch nicht.
 * - Die Sitzung wird mit `useContext` gelesen, nicht mit `useSession`: Der
 *   wirft ohne Anbieter, und eine Fehlerseite darf nicht selbst abstürzen.
 * - Die Namen der Adress-Parameter stehen ein zweites Mal in
 *   `route-error-report.ts` (gelesen werden sie in
 *   `features/feedback/model/prefill.ts`). Der Router wird sofort geladen;
 *   das Feature-Bündel mit Formular, Bildverkleinerung und Speicher soll
 *   nicht seinetwegen mitkommen. Der Test liest den Verweis mit dem echten
 *   Leser des Formulars.
 */

import { useContext, useEffect } from 'react';
import { isRouteErrorResponse, useLocation, useRouteError } from 'react-router-dom';
import { RotateCw } from 'lucide-react';

import { SessionContext } from '@entities/session';
import { createLogger } from '@shared/lib';
import { Button } from '@shared/ui/button';

import { errorText, problemReportHref } from './route-error-report';

const log = createLogger('RouteErrorPage');

export function RouteErrorPage() {
  const error = useRouteError();
  const { pathname } = useLocation();
  const session = useContext(SessionContext);
  const isEducator = session?.user?.role === 'educator';

  useEffect(() => {
    // Ein Absturz geht ins Fehlerprotokoll (`log.error` speichert ihn); eine
    // Adresse ohne Route (404 & Co.) ist kein Fehler der Anwendung und bleibt
    // in der Konsole.
    if (isRouteErrorResponse(error)) log.warn('No route for', pathname, error.status);
    else log.error('Page crashed', error);
  }, [error, pathname]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-12 text-foreground">
      <div className="w-full max-w-sm space-y-6 text-center">
        <div className="space-y-2">
          <h1 className="text-xl font-semibold">Da ist etwas schiefgegangen</h1>
          <p className="text-sm text-muted-foreground">
            Bitte lade die Seite neu. Wenn es wieder passiert, sag uns Bescheid.
          </p>
        </div>
        <div className="space-y-3">
          <Button
            type="button"
            className="min-h-[44px] w-full"
            onClick={() => window.location.reload()}
          >
            <RotateCw className="mr-2 h-4 w-4" />
            Neu laden
          </Button>
          {isEducator && (
            <Button asChild variant="outline" className="min-h-[44px] w-full">
              <a href={problemReportHref(pathname, errorText(error))}>Problem melden</a>
            </Button>
          )}
        </div>
      </div>
    </main>
  );
}
